export interface GradientValues {
    color1: string;
    color2: string;
    angle: number;
    ratio: number;
}

export class GradientColorPicker {
    private targetElement: HTMLElement;
    private callback: (value: string) => void;
    private originalValues: GradientValues = {
        color1: "#ff0000",
        color2: "#0000ff",
        angle: 90,
        ratio: 50
      };
    private values: GradientValues = {
        color1: "#ff0000",
        color2: "#0000ff",
        angle: 90,
        ratio: 50
      };
    private pickerContainer!: HTMLDivElement;
    private pickerButton!: HTMLButtonElement;
    private gradientDropdown!: HTMLDivElement;
    private gradientPreview!: HTMLDivElement;
    private color1!: HTMLInputElement;
    private color2!: HTMLInputElement;
    private angleSlider!: HTMLInputElement;
    private angleValue!: HTMLSpanElement;
    private ratioSlider!: HTMLInputElement;
    private ratioValue!: HTMLSpanElement;
    private okButton!: HTMLDivElement;
    private cancelButton!: HTMLDivElement;
    private events: any[] = [];
    constructor(targetElement: HTMLElement, defaultValue: string, callback: (value: string) => void) {
        this.targetElement = targetElement;
        this.callback = callback;
        this.setValue(defaultValue, false);
        this.init();
        this.bindEvents();
    }
    destroy(){
        this.pickerContainer.remove();
        this.gradientDropdown.remove();
        this.events.forEach((eventObj: any) => {
            eventObj.eventTarget.removeEventListener(eventObj.eventName, eventObj.callback);
        });
    }
    private bindEvents(): void {
       let eventObj = {
            eventName: "keydown",
            eventTarget: document.body,
            callback: (event: KeyboardEvent) => {
                if (event.key === "Escape") {
                    if((event.target as HTMLElement).closest(".picker-container") || (event.target as HTMLElement).closest(".gradient-dropdown")) return;   
                    this.gradientDropdown.style.display = "none";
                }
            }
        };
        (document.body as any).addEventListener(eventObj.eventName,eventObj.callback);

    }

    public setValue(defaultValue:string, refresh: boolean = true): void{
        if(defaultValue!=null){
            let pattern = /linear-gradient\(([0-9]+)deg,\s+([#0-9a-fA-F]+)\s+([0-9]+)%,\s+([#0-9a-fA-F]+)\s+100%\)/i;
            let values: RegExpMatchArray | null = null;
            if (typeof defaultValue === 'string') {
                values = defaultValue.match(pattern);
            }
            if(values!=null){
                let defaultValues: GradientValues = {
                    color1: values[2],
                    color2: values[4],
                    angle: parseInt(values[1]),
                    ratio: parseInt(values[3])
                };
                Object.assign(this.originalValues, defaultValues);
                Object.assign(this.values, defaultValues);

                if (refresh) {
                    this.updateGradient();
                }
            }
        }
    }

    private init(): void {
        const gradientCSS = `linear-gradient(${this.values.angle}deg, ${this.values.color1} ${this.values.ratio}%, ${this.values.color2} 100%)`;
        
        this.pickerContainer = document.createElement("div");
        this.pickerContainer.classList.add("picker-container");
        this.pickerContainer.style.background = gradientCSS;

        this.pickerButton = document.createElement("button");
        this.pickerButton.classList.add("picker-button");
        this.pickerButton.style.background = gradientCSS;

        this.gradientDropdown = document.createElement("div");
        this.gradientDropdown.classList.add("gradient-dropdown");
        this.gradientDropdown.style.display = "none";

        this.gradientPreview = document.createElement("div");
        this.gradientPreview.classList.add("gradient-preview");

        this.color1 = this.createColorInput(this.values.color1);
        this.color2 = this.createColorInput(this.values.color2);
        this.angleSlider = this.createSlider("angleSlider", 0, 360, this.values.angle);
        this.angleValue = document.createElement("span");
        this.ratioSlider = this.createSlider("ratioSlider", 0, 100, this.values.ratio);
        this.ratioValue = document.createElement("span");
        this.okButton = this.createButton("ok-btn", "OK", () => this.okPicker());
        this.cancelButton = this.createButton("cancel-btn", "Cancel", () => this.cancelPicker());

        this.gradientDropdown.appendChild(this.gradientPreview);
        this.gradientDropdown.appendChild(this.createControl("Color 1", this.color1));
        this.gradientDropdown.appendChild(this.createControl("Color 2", this.color2));
        this.gradientDropdown.appendChild(this.createControl("Gradient Angle", this.angleSlider, this.angleValue));
        this.gradientDropdown.appendChild(this.createControl("Color Stop Ratio", this.ratioSlider, this.ratioValue));
        this.gradientDropdown.appendChild(this.okButton);
        this.gradientDropdown.appendChild(this.cancelButton);

        this.targetElement.appendChild(this.pickerContainer);
        this.targetElement.appendChild(this.gradientDropdown);

        this.pickerContainer.addEventListener("click", () => this.togglePicker());
        this.color1.addEventListener("input", () => this.updateGradient());
        this.color2.addEventListener("input", () => this.updateGradient());
        this.angleSlider.addEventListener("input", () => this.updateGradient());
        this.ratioSlider.addEventListener("input", () => this.updateGradient());

        this.updateGradient();
    }

    private createColorInput(defaultColor: string): HTMLInputElement {
        const input = document.createElement("input");
        input.type = "color";
        input.value = defaultColor;
        return input;
    }

    private createSlider(id: string, min: number, max: number, value: number): HTMLInputElement {
        const input = document.createElement("input");
        input.type = "range";
        input.id = id;
        input.min = String(min);
        input.max = String(max);
        input.value = String(value);
        input.classList.add("slider");
        return input;
    }

    private createButton(className: string, text: string, onClick: () => void): HTMLDivElement {
        const button = document.createElement("div");
        button.classList.add(className);
        button.textContent = text;
        button.addEventListener("click", onClick);
        return button;
    }

    private createControl(labelText: string, inputElement: HTMLInputElement, valueElement: HTMLSpanElement | null = null): HTMLDivElement {
        const container = document.createElement("div");
        container.classList.add("control-group");
        const label = document.createElement("label");
        label.textContent = labelText;
        if (valueElement) {
            valueElement.textContent = inputElement.value;
            inputElement.addEventListener("input", () => {
                valueElement.textContent = inputElement.value;
            });
            label.appendChild(valueElement);
        }
        container.appendChild(label);
        container.appendChild(inputElement);
        return container;
    }

    private updateGradient(): void {
        this.values.angle = Number(this.angleSlider.value);
        this.values.ratio = Number(this.ratioSlider.value);
        this.values.color1 = this.color1.value;
        this.values.color2 = this.color2.value;

        const gradientCSS = `linear-gradient(${this.values.angle}deg, ${this.values.color1} ${this.values.ratio}%, ${this.values.color2} 100%)`;
        this.gradientPreview.style.background = gradientCSS;
        this.pickerContainer.style.background = gradientCSS;
    }
    getAbsoluteTopWithScroll(element: HTMLElement | null): number {
        let top = 0;
        let currentElement: HTMLElement | null = element;
    
        while (currentElement) {
            top += currentElement.offsetTop;
    
            // Check if the parent is scrollable and adjust for scrollTop
            let parent = currentElement.offsetParent as HTMLElement | null;
            while (parent && parent !== document.body) {
                if (parent.scrollTop) {
                    top -= parent.scrollTop; // Adjust for scrolled parents
                }
                parent = parent.parentElement as HTMLElement | null;
            }
    
            currentElement = currentElement.offsetParent as HTMLElement | null;
        }
    
        // Add the page's scroll position
        return top;
    }
    getAbsoluteLeftWithScroll(element: HTMLElement | null): number {
        let left = 0;
        let currentElement: HTMLElement | null = element;
    
        while (currentElement) {
            left += currentElement.offsetLeft;
    
            // Check if the parent is scrollable and adjust for scrollTop
            let parent = currentElement.offsetParent as HTMLElement | null;
            while (parent && parent !== document.body) {
                if (parent.scrollLeft) {
                    left -= parent.scrollLeft; // Adjust for scrolled parents
                }
                parent = parent.parentElement as HTMLElement | null;
            }
    
            currentElement = currentElement.offsetParent as HTMLElement | null;
        }
    
        // Add the page's scroll position
        return left;
    }
    private togglePicker(): void {
        this.gradientDropdown.style.display = this.gradientDropdown.style.display === "flex" ? "none" : "flex";
    
        // Get the bounding rectangle of the picker container relative to the viewport
        const pickerRect = this.pickerContainer.getBoundingClientRect();
        const dropdownRect = this.gradientDropdown.getBoundingClientRect();
        const absLeft = this.getAbsoluteLeftWithScroll(this.pickerContainer);
        const absTop = this.getAbsoluteTopWithScroll(this.pickerContainer);
        
        this.gradientDropdown.style.top = `calc(50% - ${dropdownRect.height / 2}px)`;
        this.gradientDropdown.style.left = `calc(50% - ${dropdownRect.width / 2}px)`;





        // // Calculate the dropdown's top position
        // if (pickerRect.top + pickerRect.height + dropdownRect.height > window.outerHeight) {
        //     // If the dropdown would overflow the bottom of the viewport, position it above the picker
        // } else {
        //     // Otherwise, position it below the picker
        //     this.gradientDropdown.style.top = `${absTop + pickerRect.height}px`;
        // }
    
        // // Calculate the dropdown's left position
        // if (pickerRect.left + pickerRect.width + dropdownRect.width > window.outerWidth) {
        //     // If the dropdown would overflow the right of the viewport, align it to the right
        //     this.gradientDropdown.style.left = `${absLeft + pickerRect.width - dropdownRect.width}px`;
        // } else {
        //     // Otherwise, align it to the left of the picker
        //     this.gradientDropdown.style.left = `${absLeft}px`;
        // }
    }
    private okPicker(): void {
        this.gradientDropdown.style.display = "none";
        this.originalValues = { ...this.values };
        const gradientCSS = `linear-gradient(${this.values.angle}deg, ${this.values.color1} ${this.values.ratio}%, ${this.values.color2} 100%)`;
        this.callback(gradientCSS);
    }

    private cancelPicker(): void {
        this.values = { ...this.originalValues };
        this.color1.value = this.originalValues.color1;
        this.color2.value = this.originalValues.color2;
        this.angleSlider.value = String(this.originalValues.angle);
        this.ratioSlider.value = String(this.originalValues.ratio);
        this.updateGradient();
        this.gradientDropdown.style.display = "none";
    }

}